'use client';

import { useCallback } from 'react';
import Image from 'next/image';
import { motion, useReducedMotion } from 'framer-motion';
import BgOrbs from './BgOrbs';
import { tryCtaScrollToFinal } from '../lib/tryNavigation';

/**
 * SCENE 7: Обложки и смыслы. Clean-стиль, карточки 4:5 с реальными обложками.
 */

const COVERS = [
  { kind: 'фильм',  title: 'Форест Гамп',          source: 'Маша',         from: '#e57b7b', cover: '/landing/covers/film-forrest.webp' },
  { kind: 'книга',  title: '«1984»',               source: 'Книги до 30',  from: '#5a7558', cover: '/landing/covers/book-1984.webp' },
  { kind: 'игра',   title: 'Hollow Knight',        source: 'Антон',        from: '#7b8be5', cover: '/landing/covers/game-hollow.webp' },
  { kind: 'место',  title: 'Stars Coffee',         source: 'Лена',         from: '#6b9e7f', cover: '/landing/covers/place-coffee.webp' },
];

export default function Scene07Covers() {
  const reduced = useReducedMotion() ?? false;

  const goToSwipe = useCallback(() => {
    document.getElementById('scene-swipe')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const goToFinal = useCallback(() => {
    tryCtaScrollToFinal();
  }, []);

  return (
    <section className="relative min-h-screen w-full overflow-hidden bg-[#e7f0e5] flex items-center">
      <BgOrbs variant="subtle" />

      <div className="relative max-w-6xl mx-auto w-full grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-16 items-center px-5 sm:px-8 py-20 lg:py-24">
        {/* Левая — текст */}
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col items-start gap-7"
        >
          <h2
            className="font-display font-bold text-[#161d16] leading-[0.98] tracking-[-0.02em]"
            style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)' }}
          >
            Подтягиваем обложки<br />
            <span className="text-[#b7102a]">и смыслы.</span>
          </h2>
          <p className="font-body text-[#4a5a49] text-base sm:text-lg leading-relaxed max-w-md">
            Пара секунд — и каталог листается как Netflix, а не как свалка текстов. Все записи получают удобные кнопки быстрого поиска. Для книг, музыки и игр подгружаются обложки, к фильмам — ещё и ссылки для просмотра. А для мест найдутся не только фотографии, но и приложится маршрут.
          </p>
          <div className="flex flex-col items-start gap-5">
            <button
              type="button"
              onClick={goToFinal}
              className="group relative inline-flex items-center gap-2.5 px-7 py-4 rounded-2xl bg-[#b7102a] hover:bg-[#991020] active:scale-[0.98] text-white font-semibold text-base transition-all cursor-pointer font-body"
              style={{ boxShadow: '0 12px 32px -8px rgba(183,16,42,0.45)' }}
            >
              <span>Попробовать сейчас</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="transition-transform group-hover:translate-x-0.5">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </button>
            <button
              type="button"
              onClick={goToSwipe}
              className="group inline-flex items-center gap-2 text-[#4a5a49] hover:text-[#161d16] font-body text-sm font-medium transition-colors"
            >
              <span>Как это разобрать?</span>
              <motion.svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                animate={reduced ? {} : { y: [0, 4, 0] }}
                transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
              >
                <path d="M12 5v14M19 12l-7 7-7-7" />
              </motion.svg>
            </button>
          </div>
        </motion.div>

        {/* Правая — мокап карточек */}
        <motion.div
          initial={reduced ? false : { opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="grid grid-cols-2 gap-3 sm:gap-4"
        >
          {COVERS.map((c, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl overflow-hidden border border-[#dce5d9]"
              style={{ boxShadow: '0 16px 32px -16px rgba(20,40,20,0.18)' }}
            >
              <div
                className="aspect-[4/5] relative overflow-hidden border-b border-[#e7f0e5]"
                style={{ background: `linear-gradient(135deg, ${c.from}33 0%, ${c.from}11 100%)` }}
              >
                <Image
                  src={c.cover}
                  alt={`${c.kind}: ${c.title}`}
                  fill
                  sizes="(max-width: 640px) 45vw, (max-width: 1024px) 30vw, 280px"
                  className="object-cover"
                />
              </div>
              <div className="p-3">
                <div className="font-body text-[10px] text-[#7a8a79] mb-1 truncate">{c.source}</div>
                <div className="font-body text-[#161d16] text-[13px] font-semibold leading-snug">{c.title}</div>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
